import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Globe, ChevronDown, Check } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";

const LanguageSwitcherContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const LanguageCode = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: color 0.2s ease;
`;

const LanguageButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: none;
  border: none;
  color: var(--text-color);
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 6px;
  min-width: 44px;
  min-height: 44px;
  justify-content: center;

  &:hover {
    background: var(--background-color);
    color: var(--primary-color);

    ${LanguageCode} {
      color: var(--primary-color);
    }
  }
`;

const Dropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: 0 4px 12px var(--shadow-color);
  z-index: 1000;
  min-width: 150px;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transform: ${(props) =>
    props.$isOpen ? "translateY(0)" : "translateY(-8px)"};
  transition: all 0.2s ease;
`;

const LanguageOption = styled.button<{
  $isActive?: boolean;
  $isAvailable?: boolean;
}>`
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  color: ${(props) =>
    props.$isAvailable ? "var(--text-color)" : "var(--text-secondary)"};
  text-align: right;
  cursor: ${(props) => (props.$isAvailable ? "pointer" : "not-allowed")};
  transition: background-color 0.3s ease;
  font-weight: 400;
  display: flex;
  align-items: center;
  justify-content: space-between;
  opacity: ${(props) => (props.$isAvailable ? "1" : "0.6")};

  &:hover {
    background: ${(props) =>
      props.$isAvailable ? "var(--background-color)" : "none"};
    color: ${(props) =>
      props.$isAvailable ? "var(--primary-color)" : "var(--text-secondary)"};
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }
`;

const CheckIcon = styled(Check)`
  color: var(--primary-color);
  width: 16px;
  height: 16px;
`;

const LanguageSwitcher: React.FC = () => {
  const { currentLanguage, changeLanguage, availableLanguages } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLanguageChange = (langCode: string) => {
    const language = availableLanguages.find((lang) => lang.code === langCode);
    if (language && language.isAvailable) {
      changeLanguage(langCode);
      setIsOpen(false);
    }
  };

  return (
    <LanguageSwitcherContainer ref={dropdownRef}>
      <LanguageButton onClick={() => setIsOpen(!isOpen)}>
        <Globe size={20} />
        <LanguageCode>{currentLanguage}</LanguageCode>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s ease",
          }}
        />
      </LanguageButton>

      <Dropdown $isOpen={isOpen}>
        {availableLanguages.map((lang) => (
          <LanguageOption
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            $isActive={lang.code === currentLanguage}
            $isAvailable={lang.isAvailable}
          >
            <span>{lang.nativeName}</span>
            {lang.code === currentLanguage && <CheckIcon />}
          </LanguageOption>
        ))}
      </Dropdown>
    </LanguageSwitcherContainer>
  );
};

export default LanguageSwitcher;
